import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cart, CartItem, UpdateCartItemPayload } from '../types';

interface CartState {
  cart: Cart;
  isLoading: boolean;
  error: string | null;
}

const initialState: CartState = {
  cart: {
    items: [],
    totalItems: 0,
    subtotal: 0,
    discount: 0,
    total: 0,
  },
  isLoading: false,
  error: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.cart.items.find(
        item => item.product.id === action.payload.product.id &&
                item.selectedColor === action.payload.selectedColor
      );

      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
        existingItem.totalPrice = existingItem.quantity * existingItem.price;
      } else {
        state.cart.items.push(action.payload);
      }

      cartSlice.caseReducers.calculateCartTotals(state);
    },

    updateCartItem: (state, action: PayloadAction<UpdateCartItemPayload>) => {
      const item = state.cart.items.find(i => i.id === action.payload.cartItemId);
      if (item) {
        item.quantity = action.payload.quantity;
        item.totalPrice = item.quantity * item.price;
        cartSlice.caseReducers.calculateCartTotals(state);
      }
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cart.items = state.cart.items.filter(item => item.id !== action.payload);
      cartSlice.caseReducers.calculateCartTotals(state);
    },

    clearCart: (state) => {
      state.cart = initialState.cart;
    },

    calculateCartTotals: (state) => {
      state.cart.totalItems = state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
      state.cart.subtotal = state.cart.items.reduce((sum, item) => sum + item.totalPrice, 0);
      state.cart.total = state.cart.subtotal - state.cart.discount;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  addToCart,
  updateCartItem,
  removeFromCart,
  clearCart,
  calculateCartTotals,
  setLoading,
  setError,
} = cartSlice.actions;

export default cartSlice.reducer;
